/**
 * Product Badges JavaScript
 * Handles tooltip interactions for product badges
 * 
 * Features:
 * - Desktop: Show tooltip on hover
 * - Mobile: Show tooltip on tap
 * - Keyboard: Show tooltip on focus
 * - Proper positioning to stay within viewport
 */

(function() {
  'use strict';

  const TOOLTIP_ID = 'product-badge-tooltip';
  let tooltip = null;
  let currentBadge = null;
  let hideTimeout = null;
  let isTouchDevice = false;

  /**
   * Initialize the badge tooltip system
   */
  function init() {
    // Get or create tooltip element
    tooltip = document.getElementById(TOOLTIP_ID);
    if (!tooltip) {
      tooltip = createTooltip();
    }

    // Detect touch device
    isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    // Bind events to all badges
    const badges = document.querySelectorAll('[data-badge]');
    badges.forEach(badge => {
      bindBadgeEvents(badge);
    });

    // Close tooltip when clicking outside
    document.addEventListener('click', handleDocumentClick, true);
    document.addEventListener('keydown', handleKeydown);
  }

  /**
   * Create tooltip element if it doesn't exist
   */
  function createTooltip() {
    const el = document.createElement('div');
    el.id = TOOLTIP_ID;
    el.className = 'product-badge-tooltip';
    el.setAttribute('role', 'tooltip');
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML = `
      <span class="product-badge-tooltip__content"></span>
      <span class="product-badge-tooltip__arrow"></span>
    `;
    document.body.appendChild(el);
    return el;
  }

  /**
   * Bind events to a badge element
   */
  function bindBadgeEvents(badge) {
    const tooltipText = badge.getAttribute('data-tooltip');
    if (!tooltipText) return;

    // Mouse events (desktop)
    badge.addEventListener('mouseenter', () => showTooltip(badge));
    badge.addEventListener('mouseleave', () => hideTooltip());

    // Touch events (mobile)
    badge.addEventListener('touchstart', (e) => {
      if (currentBadge === badge && tooltip.getAttribute('aria-hidden') === 'false') {
        hideTooltip();
      } else {
        e.preventDefault();
        showTooltip(badge);
      }
    }, { passive: false });

    // Keyboard events
    badge.addEventListener('focus', () => showTooltip(badge));
    badge.addEventListener('blur', () => hideTooltip());
  }

  /**
   * Show tooltip for a badge
   */
  function showTooltip(badge) {
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      hideTimeout = null;
    }

    const tooltipText = badge.getAttribute('data-tooltip');
    if (!tooltipText || !tooltip) return;

    currentBadge = badge;

    // Set content
    const content = tooltip.querySelector('.product-badge-tooltip__content');
    if (content) {
      content.textContent = tooltipText;
    }

    // Show tooltip
    tooltip.setAttribute('aria-hidden', 'false');

    // Position tooltip
    positionTooltip(badge);
  }

  /**
   * Hide tooltip
   */
  function hideTooltip() {
    hideTimeout = setTimeout(() => {
      if (tooltip) {
        tooltip.setAttribute('aria-hidden', 'true');
      }
      currentBadge = null;
    }, 100);
  }

  /**
   * Position tooltip relative to badge
   */
  function positionTooltip(badge) {
    if (!tooltip || !badge) return;

    const badgeRect = badge.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const scrollY = window.scrollY || window.pageYOffset;
    const scrollX = window.scrollX || window.pageXOffset;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Default position: above the badge
    let top = badgeRect.top + scrollY - tooltipRect.height - 10;
    let left = badgeRect.left + scrollX + (badgeRect.width / 2) - (tooltipRect.width / 2);
    let position = 'top';

    // If not enough room above, position below
    if (badgeRect.top - tooltipRect.height - 10 < 0) {
      top = badgeRect.bottom + scrollY + 10;
      position = 'bottom';
    }

    // Keep within horizontal bounds
    if (left < 10) {
      left = 10;
    } else if (left + tooltipRect.width > viewportWidth - 10) {
      left = viewportWidth - tooltipRect.width - 10;
    }

    // On mobile, center horizontally
    if (isTouchDevice && viewportWidth < 750) {
      left = (viewportWidth - tooltipRect.width) / 2;
    }

    // Apply position
    tooltip.style.top = `${top}px`;
    tooltip.style.left = `${left}px`;
    tooltip.setAttribute('data-position', position);

    // Position arrow
    const arrow = tooltip.querySelector('.product-badge-tooltip__arrow');
    if (arrow) {
      const badgeCenterX = badgeRect.left + (badgeRect.width / 2);
      const tooltipLeftEdge = left;
      const arrowOffset = Math.max(12, Math.min(badgeCenterX - tooltipLeftEdge, tooltipRect.width - 12));
      arrow.style.left = `${arrowOffset}px`;
      arrow.style.marginLeft = '0';
    }
  }

  /**
   * Handle document click to close tooltip on mobile
   */
  function handleDocumentClick(e) {
    if (!isTouchDevice) return;
    
    // If click is not on current badge or tooltip, hide
    if (currentBadge && !currentBadge.contains(e.target) && !tooltip.contains(e.target)) {
      hideTooltip();
    }
  }

  /**
   * Handle keyboard events
   */
  function handleKeydown(e) {
    // Close tooltip on Escape
    if (e.key === 'Escape' && tooltip.getAttribute('aria-hidden') === 'false') {
      hideTooltip();
      if (currentBadge) {
        currentBadge.blur();
      }
    }
  }

  /**
   * Re-initialize badges (for dynamically loaded content)
   */
  function reinit() {
    const badges = document.querySelectorAll('[data-badge]');
    badges.forEach(badge => {
      // Check if already initialized
      if (!badge.hasAttribute('data-badge-initialized')) {
        bindBadgeEvents(badge);
        badge.setAttribute('data-badge-initialized', 'true');
      }
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose reinit for dynamic content
  window.ProductBadges = {
    reinit: reinit
  };
})();

